import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

const categoryAvatars = [
  { bg: "bg-primary-100", text: "text-primary-600", letter: "S" },
  { bg: "bg-secondary-100", text: "text-secondary-600", letter: "H" },
  { bg: "bg-accent-100", text: "text-accent-600", letter: "T" },
  { bg: "bg-primary-100", text: "text-primary-600", letter: "J" },
  { bg: "bg-secondary-100", text: "text-secondary-600", letter: "P" },
  { bg: "bg-accent-100", text: "text-accent-600", letter: "H" },
  { bg: "bg-primary-100", text: "text-primary-600", letter: "S" },
  { bg: "bg-secondary-100", text: "text-secondary-600", letter: "U" },
  { bg: "bg-accent-100", text: "text-accent-600", letter: "J" },
  { bg: "bg-primary-100", text: "text-primary-600", letter: "A" },
  { bg: "bg-secondary-100", text: "text-secondary-600", letter: "B" },
  { bg: "bg-accent-100", text: "text-accent-600", letter: "B" },
  { bg: "bg-primary-100", text: "text-primary-600", letter: "P" },
  { bg: "bg-secondary-100", text: "text-secondary-600", letter: "O" },
];

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <header className="relative w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-heading text-xl font-bold text-foreground-950">
                Hipobuy<span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            {/* Center: Dropdown */}
            <div className="hidden md:flex items-center gap-4" ref={dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-background-300 hover:border-primary-300 bg-background-50 text-foreground-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                    <div className="max-h-80 overflow-y-auto py-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-lg bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Hipobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-lg border border-foreground-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg border border-background-300 text-foreground-700 cursor-pointer"
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-background-50/98 backdrop-blur-xl flex flex-col animate-[fadeIn_0.2s_ease-out]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200 flex-shrink-0">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-heading text-xl font-bold text-foreground-950">
                Hipobuy<span className="text-primary-500">Sheet</span>
              </span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-background-300 text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-4">
              Categories
            </p>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat, i) => {
                const avatar = categoryAvatars[i];
                return (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex flex-col items-center gap-2.5 p-4 rounded-xl bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <div className={`w-11 h-11 rounded-full ${avatar.bg} ${avatar.text} flex items-center justify-center text-sm font-bold`}>
                      {avatar.letter}
                    </div>
                    <span className="text-xs font-semibold text-foreground-700 text-center leading-tight">
                      {cat.name}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Bottom CTAs */}
          <div className="px-4 py-4 border-t border-background-200 flex gap-3 flex-shrink-0">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 px-4 py-3 rounded-xl bg-foreground-900 text-background-50 text-sm font-semibold text-center cursor-pointer whitespace-nowrap"
            >
              Hipobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 px-4 py-3 rounded-xl border border-foreground-300 text-foreground-800 text-sm font-medium text-center cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}

      {/* Hero Content */}
      <div className="flex-1 flex flex-col lg:flex-row items-center pt-16">
        {/* Left Content */}
        <div className="w-full lg:w-5/12 px-4 md:px-6 lg:pl-12 xl:pl-20 py-12 md:py-16 flex flex-col justify-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-tight">
            Hipobuy
            <br />
            <span className="italic font-semibold text-primary-500">Spreadsheet</span> Hub
          </h1>
          <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-lg">
            The ultimate shopping spreadsheet resource center. Discover organized product lists, compare spreadsheets, and find products faster with expert guides designed for smart shoppers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Hipobuy Sheet
              <i className="ri-arrow-right-line"></i>
            </a>
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-foreground-300 text-foreground-800 font-medium text-sm hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              View Latest Guides
              <i className="ri-article-line"></i>
            </Link>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-4 mt-8">
            {["Updated Resources", "Beginner Friendly", "Detailed Comparisons", "Safety Information"].map((badge) => (
              <span key={badge} className="flex items-center gap-1.5 text-xs text-foreground-500 whitespace-nowrap">
                <i className="ri-checkbox-circle-fill text-primary-500 text-sm"></i>
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Right Visual */}
        <div className="w-full lg:w-7/12 h-[400px] md:h-[550px] lg:h-screen relative overflow-hidden bg-gradient-to-br from-primary-50/50 via-background-50 to-accent-50/40">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-full h-full max-w-2xl">
              {/* Decorative product image collage */}
              <div className="absolute top-[10%] left-[8%] w-36 h-44 md:w-44 md:h-52 rounded-2xl overflow-hidden border-4 border-background-50 shadow-sm rotate-[-3deg]">
                <img
                  src="https://readdy.ai/api/search-image?query=Bold%20colorful%20gradient%20running%20sneakers%20on%20vibrant%20skate%20park%20concrete%20ramp%20with%20graffiti%20wall%20background%2C%20bright%20daylight%2C%20dynamic%20street%20culture%20photography%2C%20saturated%20colors%2C%20energetic%20youth%20aesthetic&width=400&height=480&seq=hero-shoes-v2-01&orientation=portrait"
                  alt="Sneakers"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute top-[25%] right-[10%] w-32 h-40 md:w-40 md:h-48 rounded-2xl overflow-hidden border-4 border-background-50 shadow-sm rotate-[2deg]">
                <img
                  src="https://readdy.ai/api/search-image?query=Structured%20leather%20crossbody%20bag%20placed%20on%20marble%20cafe%20table%20next%20to%20ceramic%20coffee%20cup%20and%20fresh%20croissant%2C%20European%20bistro%20aesthetic%2C%20warm%20morning%20light%20through%20window%2C%20cozy%20lifestyle%20photography&width=400&height=480&seq=hero-bag-v2-02&orientation=portrait"
                  alt="Handbag"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute bottom-[15%] left-[20%] w-40 h-36 md:w-48 md:h-44 rounded-2xl overflow-hidden border-4 border-background-50 shadow-sm rotate-[1deg]">
                <img
                  src="https://readdy.ai/api/search-image?query=Oversized%20denim%20jacket%20caught%20mid-swing%20on%20clothesline%20against%20bright%20blue%20sky%20with%20fluffy%20white%20clouds%2C%20windy%20day%20motion%20blur%2C%20carefree%20summer%20aesthetic%2C%20vivid%20natural%20colors%2C%20outdoor%20fashion%20photography&width=480&height=400&seq=hero-jacket-v2-03&orientation=landscape"
                  alt="Jacket"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute bottom-[20%] right-[15%] w-28 h-36 md:w-36 md:h-44 rounded-2xl overflow-hidden border-4 border-background-50 shadow-sm rotate-[-2deg]">
                <img
                  src="https://readdy.ai/api/search-image?query=Graphic%20print%20cotton%20t-shirt%20hanging%20on%20wooden%20clothespin%20on%20rustic%20rope%20clothesline%20with%20beach%20and%20ocean%20waves%20in%20background%2C%20tropical%20vacation%20aesthetic%2C%20bright%20golden%20sunlight%2C%20breezy%20summer%20photography&width=360&height=440&seq=hero-tshirt-v2-04&orientation=portrait"
                  alt="T-Shirt"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Decorative blobs */}
              <div className="absolute top-[40%] left-[35%] w-20 h-20 rounded-full bg-primary-200/30 blur-xl"></div>
              <div className="absolute bottom-[35%] right-[30%] w-16 h-16 rounded-full bg-accent-200/40 blur-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}